import { rotateArray } from '../../utils/array';
import { getResponsivePlaceholderThumbnail } from '../../utils/image';
import { capitalizeString } from '../../utils/string';
import { getResponsiveTrackThumbnail, getTrackAssets, getTrackDuration } from '../../utils/track';

import { filter, find, findIndex, first, lowerCase, memoize, nth, reduce } from 'lodash';
import { createSelector } from 'reselect';

import { getQueue } from '../selectors/player';

export const getLongestDuration = (tracks: IPlayerTrack[]) =>
  reduce(tracks, (d, { trackInfo: { duration } }) => !!duration ? duration > d ? duration : d : d, 0);

export const getTrackInQueueByVersionId = (versionId: string) => createSelector(
  getQueue,
  (queue) => {
    return find(queue, { versionId });
  },
);

export const getTrackInQueueById = createSelector(
  getQueue,
  (queue) => memoize((id: string) => {
    const foundTracks = filter(queue, (track) => {
      return track.trackId === id || track.versionId === id;
    });

    if (foundTracks.length > 1) {
      const longuestDuration = getLongestDuration(foundTracks);
      return foundTracks.find(({ trackInfo: { assets, duration } }) =>
        !!find(assets, { id, version: 'original' }) || duration === longuestDuration);
    }

    return first(foundTracks);
  }),
);

export const getTrackInQueueByIndex = createSelector(
  getQueue,
  (queue) => memoize((trackIndex: number) => nth(queue, trackIndex)),
);

export const getTrackVersion = (trackVersionId: string) => createSelector(
  getTrackInQueueByVersionId(trackVersionId),
  (track: IPlayerTrack) => track && find(track.trackInfo.assets, { id: trackVersionId }),
);

export const getTrackVersionType = (trackVersionId: string) => createSelector(
  getTrackInQueueByVersionId(trackVersionId),
  getTrackVersion(trackVersionId),
  (track, version) => version ? version.version! : '',
);

export const getTrackAudioFile = (trackVersionId: string) => createSelector(
  getTrackInQueueByVersionId(trackVersionId),
  getTrackVersion(trackVersionId),
  (track, version) => version ? version.path! : '',
);

export const getTrackTitle = (trackVersionId: string) => createSelector(
  getTrackInQueueByVersionId(trackVersionId),
  getTrackVersion(trackVersionId),
  (track, version) => {
    if (track) {
      return version && lowerCase(version.version!) !== 'original' ?
        `${track.trackInfo.title!} (${capitalizeString(version.version!)} Version)`
        : track.trackInfo.title!;
    }

    return '';
  },
);

export const getTrackArtist = (trackVersionId: string) => createSelector(
  getTrackInQueueByVersionId(trackVersionId),
  (track) => {
    if (track) {
      return track.trackInfo.artist!.name || '';
    }

    return '';
  },
);

export function changeTrack(state: IPlayerState, track: number | string) {
  const newState: IPlayerState = { ...state, isTrackPlaying: true };

  const nextTrack: IPlayerTrack | undefined = typeof track === 'string' ?
    getTrackInQueueByVersionId(track)(state)
    : getTrackInQueueByIndex(state)(track - 1);

  if (nextTrack) {
    newState.currentTrack = {
      ...nextTrack,
      artist: getTrackArtist(nextTrack.versionId)(state),
      audioFile: getTrackAudioFile(nextTrack.versionId)(state),
      title: getTrackTitle(nextTrack.versionId)(state),
      version: getTrackVersionType(nextTrack.versionId)(state),
    };
    const trackIndexInQueue = findIndex(state.queue, { versionId: nextTrack.versionId });
    const trackIndexInHistory = findIndex(state.history, { versionId: nextTrack.versionId });

    // If changing track sequentially, we make sure to play only original versions
    // by skipping the other versions
    if (state.shouldOnlyPlayOriginalTracks) {
      if (typeof track === 'number' && lowerCase(newState.currentTrack.version) !== 'original') {
        return changeTrack(state, track > 0 ? track + 1 : track - 1);
      }
    }

    // If the currently playing track is not an original version, we ensure that the next
    // track in queue is not the same track with a different version.
    // Else we skip to the next track in queue (+2)
    // if (state.currentTrack && newState.currentTrack.trackId === state.currentTrack.trackId) {
    //   console.log('skip to next track');
    //   return changeTrack(state, trackIndexInQueue + 2);
    // }

    if (trackIndexInHistory === -1) {
      newState.history = [...state.history, nextTrack];
    }

    newState.queue = rotateArray([...state.queue], trackIndexInQueue + 1);
  }

  return newState;
}

export function getPlayerTracksFromResults(tracks: GQL.ITrackSearchResult[], onlyMP3 = true) {
  const playerTracks: IPlayerTrack[] = [];
  return reduce<GQL.ITrackSearchResult, IPlayerTrack[]>(tracks, (acc, track) => {
    let versions = getTrackAssets({
      assets: track.assets!,
      format: onlyMP3 ? 'mp3' : undefined,
      type: 'track-preview',
    });

    if (!versions.length) {
      versions = getTrackAssets({
        assets: track.assets!,
        format: onlyMP3 ? 'mp3' : undefined,
        type: 'track',
      });
    }

    const imageAssets = getResponsiveTrackThumbnail(track);
    // HOTFIX: this is a temporary hack as this object is immutable and we need to ensure
    // the redicts for assets aren't cached in the latest version of Chrome on macos
    try {
      track = JSON.parse(JSON.stringify(track));
      track.assets!.forEach((a) => {
        a.path = `${a.path}?ts=${new Date().getTime()}`;
      });
    } catch (e) {
      console.error(e);
    }
    versions.forEach((version) => {
      acc.push({
        duration: getTrackDuration({ track, version }),
        elapsedTime: 0,
        imageBlurred: imageAssets.blur ? imageAssets.blur[0].path : null,
        image: imageAssets.small ? imageAssets.small[0].path : getResponsivePlaceholderThumbnail().small[0].path,
        versionId: version.id,
        trackId: track.id,
        trackInfo: track,
      });
    });
    return acc;
  }, playerTracks);
}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--0!./src/public/redux/helpers/player.ts